import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import { FileUpload } from "@/components/ui/file-upload";
import { useAuth } from "@/contexts/auth-context";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  UsersIcon,
  UserIcon,
  ImageIcon,
  PlusIcon,
  InfoIcon,
} from "lucide-react";
import { useCreateTeam, useOrganizationMembers } from "@/hooks/api-hooks";
import { toast } from "react-hot-toast";

// Form schema
const teamFormSchema = z.object({
  name: z.string().min(2, "Team name must be at least 2 characters"),
  description: z.string().optional(),
  managerId: z.string().min(1, "Please select a team manager"),
  leadId: z.string().min(1, "Please select a team lead"),
  members: z.array(z.string()).optional(),
});

type TeamFormValues = z.infer<typeof teamFormSchema>;

type UserOption = { value: string; label: string; disabled?: boolean };

export function TeamCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teamLogo, setTeamLogo] = useState<File | null>(null);

  // Create form
  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamFormSchema),
    defaultValues: {
      name: "",
      description: "",
      managerId: "",
      leadId: "",
      members: [],
    },
  });

  // Fetch organization members
  const { data: organizationMembersResponse, isLoading: isLoadingMembers, error: membersError } = useOrganizationMembers(
    user?.currentOrganizationId || '',
    { isActive: true }
  );

  const createTeamMutation = useCreateTeam({
    onSuccess: () => {
      toast.success('Team created successfully!');
      navigate('/team');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create team');
    },
  });

  // Filter users for current organization
  const membersLoadError = membersError instanceof Error ? membersError.message : null;

  const organizationUsers = useMemo<UserOption[]>(() => {
    if (!organizationMembersResponse) {
      return [];
    }

    const rawMembers = Array.isArray((organizationMembersResponse as any).data)
      ? (organizationMembersResponse as any).data
      : Array.isArray((organizationMembersResponse as any)?.data?.data)
      ? (organizationMembersResponse as any).data.data
      : [];

    return rawMembers
      .filter((member: any) => {
        if (!member || typeof member !== 'object') return false;

        const isInvitation = Boolean(member.isInvitation ?? member.is_invitation);
        if (isInvitation) return false;

        const memberIsActive = member.isActive ?? member.is_active;
        if (typeof memberIsActive === 'boolean') {
          return memberIsActive;
        }

        const userIsActive = member.user?.isActive ?? member.user?.is_active;
        if (typeof userIsActive === 'boolean') {
          return userIsActive;
        }

        return true;
      })
      .filter((member: any) => member?.user?.id)
      .map((member: any) => {
        const user = member.user ?? {};
        const nameFromParts = [user.firstName ?? user.first_name, user.lastName ?? user.last_name]
          .filter(Boolean)
          .join(' ')
          .trim();
        const primaryName = typeof user.name === 'string' ? user.name.trim() : '';
        const displayName = primaryName || nameFromParts || user.email || 'Unknown member';

        const rawRole = (member.role ?? member.organizationRole ?? '').toString();
          const formattedRole = rawRole
            ? rawRole
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/(^|\s)\w/g, (char: string) => char.toUpperCase())
            : '';

        return {
          value: user.id as string,
          label: formattedRole ? `${displayName} (${formattedRole})` : displayName,
        };
      });
  }, [organizationMembersResponse]);

  const managerId = form.watch('managerId');
  const leadId = form.watch('leadId');

  const memberIds = form.watch('members') || [];

  const handleManagerChange = (value: string) => {
    const currentLead = form.getValues('leadId');
    const currentMembers = form.getValues('members') || [];

    if (value === currentLead && value) {
      form.setValue('leadId', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      toast.error('Team lead must be different from manager. Previous lead selection was cleared.', {
        id: 'team-role-conflict',
      });
    }

    const filteredMembers = currentMembers.filter((memberId: string) => memberId !== value);
    if (filteredMembers.length !== currentMembers.length) {
      form.setValue('members', filteredMembers, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    form.setValue('managerId', value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleLeadChange = (value: string) => {
    const currentManager = form.getValues('managerId');
    const currentMembers = form.getValues('members') || [];

    if (value === currentManager) {
      toast.error('Team lead cannot also be the manager. Please choose a different lead.', {
        id: 'team-role-conflict',
      });
      return;
    }

    const filteredMembers = currentMembers.filter((memberId: string) => memberId !== value);
    if (filteredMembers.length !== currentMembers.length) {
      form.setValue('members', filteredMembers, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    form.setValue('leadId', value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleMembersChange = (values: string[]) => {
    const uniqueMembers = Array.from(new Set(values));
    const { managerId: currentManager, leadId: currentLead } = form.getValues();

    const sanitizedMembers = uniqueMembers.filter(
      (memberId) => memberId !== currentManager && memberId !== currentLead
    );

    if (sanitizedMembers.length !== uniqueMembers.length) {
      toast.error('Assigned manager and lead are automatically part of the team and do not need to be added as members.', {
        id: 'team-role-conflict',
      });
    }

    form.setValue('members', sanitizedMembers, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // Handle form submission
  const onSubmit = (values: TeamFormValues) => {
    const uniqueMembers = Array.from(new Set(values.members ?? [])).filter(
      (memberId) => memberId !== values.managerId && memberId !== values.leadId
    );

    const teamData = {
      name: values.name,
      description: values.description || undefined,
      managerId: values.managerId || undefined,
      leadId: values.leadId || undefined,
      members: uniqueMembers.length ? uniqueMembers : undefined,
    };

    createTeamMutation.mutate(teamData);
  };

  const leadOptions = useMemo(
    () =>
      organizationUsers.map((option: UserOption) => ({
        ...option,
        disabled: option.value === managerId,
      })),
    [organizationUsers, managerId]
  );

  const memberOptions = useMemo(
    () =>
      organizationUsers.map((option: UserOption) => ({
        ...option,
        disabled: option.value === managerId || option.value === leadId,
      })),
    [organizationUsers, managerId, leadId]
  );

  return (
    <div className="space-y-8 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl flex items-center justify-center shadow-clean">
            <PlusIcon className="h-10 w-10 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-foreground tracking-tight mb-2">Create New Team</h1>
            <p className="text-xl text-muted-foreground">
              Build your team structure and assign team members
            </p>
            <div className="text-sm text-muted-foreground mt-2">
              2 of 6 teams
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate(-1)} className="gap-2 rounded-xl h-12 px-6">
            <ArrowLeftIcon className="h-4 w-4" />
            Cancel
          </Button>
        </div>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {/* Team Information */}
        <Card className="p-8 rounded-3xl border border-border shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl flex items-center justify-center">
              <InfoIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-foreground">Team Information</h3>
              <p className="text-muted-foreground">Set up the basic details for your new team</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Team Logo Section */}
            <div className="lg:col-span-1">
              <div className="p-6 bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                  <ImageIcon className="h-5 w-5 text-gray-600" />
                  <Label className="text-sm font-semibold text-foreground">Team Logo</Label>
                </div>
                <FileUpload
                  onFileChange={setTeamLogo}
                  value={teamLogo}
                  accept="image/*"
                  maxSize={2 * 1024 * 1024} // 2MB
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Upload a logo to represent your team. Max size: 2MB
                </p>
              </div>
            </div>

            {/* Team Details */}
            <div className="lg:col-span-2 space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">
                  Team Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  {...form.register("name")}
                  placeholder="e.g., Engineering Team, Marketing Squad"
                  className="h-12 rounded-xl border-2 border-border focus:border-primary focus:ring-0"
                />
                {form.formState.errors.name && (
                  <p className="text-destructive text-sm">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground">Team Description</Label>
                <Textarea
                  {...form.register("description")}
                  placeholder="Describe the team's purpose, goals, and responsibilities..."
                  rows={4}
                  className="rounded-xl border-2 border-border focus:border-primary focus:ring-0"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Team Structure */}
        <Card className="p-8 rounded-3xl border border-border shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl flex items-center justify-center">
              <UsersIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-foreground">Team Structure</h3>
              <p className="text-muted-foreground">Assign a manager and select team members</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Manager Selection */}
            <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
              <div className="flex items-center gap-2 mb-4">
                <UserIcon className="h-5 w-5 text-primary" />
                <Label className="text-sm font-semibold text-foreground">Team Manager</Label>
                <span className="text-destructive">*</span>
              </div>
              {isLoadingMembers ? (
                <div className="text-sm text-muted-foreground">Loading organization members...</div>
              ) : membersLoadError ? (
                <div className="text-sm text-destructive">Error loading members: {membersLoadError}</div>
              ) : organizationUsers.length === 0 ? (
                <div className="text-sm text-muted-foreground">No active organization members available.</div>
              ) : (
                <Select value={managerId || undefined} onValueChange={handleManagerChange}>
                  <SelectTrigger className="h-12 rounded-xl">
                    <SelectValue placeholder="Select team manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizationUsers.map((user: UserOption) => (
                      <SelectItem key={user.value} value={user.value}>
                        {user.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {form.formState.errors.managerId && (
                <p className="text-destructive text-sm mt-1">
                  {form.formState.errors.managerId.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                The manager will oversee team operations and member development
              </p>
            </div>

            {/* Team Lead Selection */}
            <div className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-100">
              <div className="flex items-center gap-2 mb-4">
                <UserIcon className="h-5 w-5 text-orange-600" />
                <Label className="text-sm font-semibold text-foreground">Team Lead</Label>
                <span className="text-destructive">*</span>
              </div>
              {isLoadingMembers ? (
                <div className="text-sm text-muted-foreground">Loading organization members...</div>
              ) : membersLoadError ? (
                <div className="text-sm text-destructive">Error loading members: {membersLoadError}</div>
              ) : organizationUsers.length === 0 ? (
                <div className="text-sm text-muted-foreground">No active organization members available.</div>
              ) : (
                <Select value={leadId || undefined} onValueChange={handleLeadChange}>
                  <SelectTrigger className="h-12 rounded-xl">
                    <SelectValue placeholder="Select team lead" />
                  </SelectTrigger>
                  <SelectContent>
                    {leadOptions.map((user: UserOption) => (
                      <SelectItem key={user.value} value={user.value} disabled={user.disabled}>
                        {user.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {form.formState.errors.leadId && (
                <p className="text-destructive text-sm mt-1">
                  {form.formState.errors.leadId.message}
                </p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                The team lead will coordinate team activities and serve as the primary point of contact
              </p>
            </div>
          </div>

          {/* Team Members Multi-Select */}
          <div className="mt-8">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-foreground">Team Members</Label>
              {isLoadingMembers ? (
                <div className="text-sm text-muted-foreground">Loading organization members...</div>
              ) : membersLoadError ? (
                <div className="text-sm text-destructive">Error loading members: {membersLoadError}</div>
              ) : organizationUsers.length === 0 ? (
                <div className="text-sm text-muted-foreground">No active organization members available.</div>
              ) : (
                <MultiSelect
                  options={memberOptions}
                  value={memberIds}
                  onChange={handleMembersChange}
                  placeholder="Select team members"
                  className="h-12 rounded-xl"
                />
              )}
              <p className="text-xs text-muted-foreground">
                Choose the initial members for this team. You can add more members later.
              </p>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4 pt-6 border-t border-border">
          <Button
            variant="outline"
            type="button"
            onClick={() => navigate(-1)}
            className="h-12 px-8 rounded-xl"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createTeamMutation.isPending || isLoadingMembers}
            className="h-12 px-8 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 rounded-xl disabled:opacity-50"
          >
            {createTeamMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Creating Team...
              </>
            ) : (
              <>
                <CheckCircleIcon className="mr-2 h-4 w-4" />
                Create Team
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
